import { useState } from 'react'
import { useFinance } from '../context/FinanceContext'
import { useSettings } from '../context/SettingsContext'
import { getThemeColors } from '../lib/constants'
import { trackCalculatorUse } from '../lib/analytics'
import PageShell from '../components/layout/PageShell'
import InvestmentCalculator from '../components/scenarios/InvestmentCalculator'
import FIRECalculator from '../components/scenarios/FIRECalculator'
import SocialSecurity from '../components/scenarios/SocialSecurity'
import RetirementCalculator from '../components/scenarios/RetirementCalculator'
import RothVsTraditional from '../components/scenarios/RothVsTraditional'
import CapitalGainsTax from '../components/scenarios/CapitalGainsTax'

const TABS = [
  { key: 'investments', label: 'Investment Growth', icon: InvestmentIcon },
  { key: 'fire', label: 'FIRE', icon: FireIcon },
  { key: 'socialsecurity', label: 'Social Security', icon: SSIcon },
  { key: 'retirement', label: 'STRS / Pension', icon: RetirementIcon },
  { key: 'rothvstrad', label: 'Roth vs Trad', icon: RothIcon },
  { key: 'capitalgains', label: 'Capital Gains', icon: TrendingIcon },
]

export default function InvestingRetirement() {
  const { state } = useFinance()
  const { trackCalculatorOpen } = useSettings()
  const colors = getThemeColors(state.theme)
  const validTabs = TABS.map(t => t.key)
  const [activeTab, setActiveTab] = useState(validTabs.includes(state.activeTab) ? state.activeTab : 'investments')

  return (
    <PageShell
      title="Investing & Retirement"
      subtitle="Investment growth, FIRE planning, Social Security, pensions, Roth vs Traditional, and capital gains"
    >
      <div className="flex gap-1 p-1 rounded-2xl glass mb-6 max-w-fit overflow-x-auto">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => { setActiveTab(key); trackCalculatorUse(key); trackCalculatorOpen(key) }}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 whitespace-nowrap ${
              activeTab === key
                ? 'text-heading shadow-sm'
                : 'text-muted hover:text-heading'
            }`}
            style={activeTab === key ? {
              background: `linear-gradient(135deg, ${colors.primary}22, ${colors.secondary}22)`,
              border: `1px solid ${colors.primary}33`,
            } : {}}
          >
            <Icon active={activeTab === key} />
            <span className="hidden sm:inline">{label}</span>
          </button>
        ))}
      </div>

      <div className="animate-fade-in">
        {activeTab === 'investments' && <InvestmentCalculator />}
        {activeTab === 'fire' && <FIRECalculator />}
        {activeTab === 'socialsecurity' && <SocialSecurity />}
        {activeTab === 'retirement' && <RetirementCalculator />}
        {activeTab === 'rothvstrad' && <RothVsTraditional />}
        {activeTab === 'capitalgains' && <CapitalGainsTax />}
      </div>
    </PageShell>
  )
}

function InvestmentIcon({ active }) {
  return (
    <svg className="w-4 h-4" style={active ? { color: 'var(--primary)' } : {}} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
    </svg>
  )
}

function FireIcon({ active }) {
  return (
    <svg className="w-4 h-4" style={active ? { color: 'var(--primary)' } : {}} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.362 5.214A8.252 8.252 0 0112 21 8.25 8.25 0 016.038 7.048 8.287 8.287 0 009 9.6a8.983 8.983 0 013.361-6.867 8.21 8.21 0 003 2.48z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 18a3.75 3.75 0 00.495-7.467 5.99 5.99 0 00-1.925 3.546 5.974 5.974 0 01-2.133-1.001A3.75 3.75 0 0012 18z" />
    </svg>
  )
}

function SSIcon({ active }) {
  return (
    <svg className="w-4 h-4" style={active ? { color: 'var(--primary)' } : {}} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
    </svg>
  )
}

function RetirementIcon({ active }) {
  return (
    <svg className="w-4 h-4" style={active ? { color: 'var(--primary)' } : {}} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  )
}

function RothIcon({ active }) {
  return (
    <svg className="w-4 h-4" style={active ? { color: 'var(--primary)' } : {}} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
    </svg>
  )
}

function TrendingIcon({ active }) {
  return (
    <svg className="w-4 h-4" style={active ? { color: 'var(--primary)' } : {}} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941" />
    </svg>
  )
}
